import {
  type PropsWithChildren,
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  type FilterValue,
  type Filter,
} from "@shopify/hydrogen-react/storefront-api-types";

import { useCollectionSortOptions } from "@react/hooks/useCollectionSortOptions";
import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { generatePriceFilterValue } from "@react/utilities/collection";
import {
  getAppliedFiltersFromQuery,
  getAppliedSortFromQuery,
  updateQueryFromFilterSortState,
} from "@react/utilities/collectionFilters";
import {
  type PriceRange,
  type SortOption,
  type CollectionSectionSettings,
} from "@tablescape/state";
import { getShopifyGlobalId } from "@tablescape/utilities";

export type CollectionFiltersContextValues = {
  addFilter: (filter: FilterValue) => void;
  appliedFilters: FilterValue[];
  appliedSort: SortOption | undefined;
  availableFilters: Filter[] | undefined;
  availableSorts: SortOption[];
  clearFilters: () => void;
  collectionPriceMax: number;
  hasCompletedFirstRender: boolean;
  markFirstRenderComplete: () => void;
  onPriceRangeChange: (range: PriceRange) => void;
  removeFilter: (filter: FilterValue) => void;
  removePriceFilter: () => void;
  setAvailableFilters: Dispatch<SetStateAction<Filter[] | undefined>>;
  setCollectionPriceMax: Dispatch<SetStateAction<number>>;
  updateAppliedSort: (sort: SortOption) => void;
};

const CollectionFiltersContext = createContext<
  CollectionFiltersContextValues | undefined
>(undefined);

type Props = {
  value?: CollectionFiltersContextValues;
};

function CollectionFiltersProvider({
  children,
  value: providedValue,
}: PropsWithChildren<Props>) {
  const { collectionId } = useSectionSettings<CollectionSectionSettings>();

  const hasCompletedFirstRender = useRef(false);
  const hasHydrated = useRef(false);

  const { sortOptions } = useCollectionSortOptions(
    getShopifyGlobalId("Collection", collectionId)
  );

  const [availableFilters, setAvailableFilters] = useState<
    Filter[] | undefined
  >(undefined);

  const [appliedFilters, setAppliedFilters] = useState<FilterValue[]>([]);

  const [availableSorts, setAvailableSorts] = useState<SortOption[]>(
    sortOptions ?? []
  );

  const [appliedSort, setAppliedSort] = useState<SortOption | undefined>(
    sortOptions?.[0] ?? undefined
  );

  const [collectionPriceMax, setCollectionPriceMax] = useState<number>(0);

  const markFirstRenderComplete = useCallback(() => {
    hasCompletedFirstRender.current = true;
  }, []);

  useEffect(() => {
    if (sortOptions) {
      setAvailableSorts(sortOptions);
      setAppliedSort(sortOptions[0]);
    }
  }, [sortOptions]);

  useEffect(() => {
    if (!hasHydrated.current && availableFilters) {
      const preservedFilters = getAppliedFiltersFromQuery(availableFilters);
      if (preservedFilters.length > 0) setAppliedFilters(preservedFilters);

      const preservedSort = getAppliedSortFromQuery(availableSorts);
      if (preservedSort) setAppliedSort(preservedSort);

      hasHydrated.current = true;
    }
  }, [availableFilters, availableSorts]);

  useEffect(() => {
    if (hasHydrated.current && availableFilters && availableSorts) {
      updateQueryFromFilterSortState(
        appliedFilters,
        availableFilters,
        appliedSort,
        availableSorts
      );
    }
  }, [appliedFilters, availableFilters, availableSorts, appliedSort]);

  const addFilter = useCallback((filter: FilterValue) => {
    setAppliedFilters((prev) => [...prev, filter]);
  }, []);

  const removeFilter = useCallback((filter: FilterValue) => {
    setAppliedFilters((prev) => prev.filter((f) => f.id !== filter.id));
  }, []);

  const removePriceFilter = useCallback(() => {
    setAppliedFilters((prev) =>
      prev.filter((f) => f.id !== COLLECTION_FILTER_IDS.PRICE)
    );
  }, []);

  const clearFilters = useCallback(() => {
    setAppliedFilters([]);
  }, []);

  const onPriceRangeChange = useCallback(
    (range: PriceRange) => {
      removePriceFilter();
      addFilter(generatePriceFilterValue(range.min, range.max ?? 0));
    },
    [removePriceFilter, addFilter]
  );

  const updateAppliedSort = useCallback((sort: SortOption) => {
    setAppliedSort(sort);
  }, []);

  const value = useMemo(
    () => ({
      ...providedValue,
      addFilter,
      appliedFilters,
      availableFilters,
      appliedSort,
      removeFilter,
      clearFilters,
      availableSorts,
      hasCompletedFirstRender: hasCompletedFirstRender.current,
      markFirstRenderComplete,
      collectionPriceMax,
      setCollectionPriceMax,
      setAvailableFilters,
      updateAppliedSort,
      onPriceRangeChange,
      removePriceFilter,
    }),
    [
      providedValue,
      addFilter,
      appliedFilters,
      availableFilters,
      appliedSort,
      removeFilter,
      clearFilters,
      availableSorts,
      hasCompletedFirstRender,
      markFirstRenderComplete,
      collectionPriceMax,
      setCollectionPriceMax,
      updateAppliedSort,
      onPriceRangeChange,
      removePriceFilter,
    ]
  );

  return (
    <CollectionFiltersContext.Provider value={value}>
      {children}
    </CollectionFiltersContext.Provider>
  );
}

function useCollectionFilters() {
  const context = useContext<CollectionFiltersContextValues | undefined>(
    CollectionFiltersContext
  );

  if (context === undefined) {
    throw new Error(
      "useCollectionFilters must be used within a CollectionFiltersProvider"
    );
  }

  return context;
}

export {
  CollectionFiltersContext,
  CollectionFiltersProvider,
  useCollectionFilters,
};
